import type { ApprovalRow } from '../../lib/database.types'
import { short, stripMarkers } from '../../lib/text'

/**
 * Whether the fleet is working, as one line for the cost strip.
 *
 * A fleet doing nothing and a fleet with nothing to do render identically
 * everywhere else in this app, which is how dispatch stayed dead for hours on
 * 20 Sep with every page looking normal. So the strip carries a sentence: not
 * a coloured dot, because a dot goes red and you still have to go and find out
 * what, and finding out is the whole point.
 *
 * **This module does not compute health.** The watchdog decides whether the
 * fleet is broken and files what it found in `approvals`; the dashboard reports
 * what the fleet said, verbatim. Detection needs Linear, this app has no Linear
 * access, and two implementations of the same check would eventually disagree
 * in front of the two people who are meant to trust this screen.
 */

/**
 * The contract with the watchdog, and the only thing that identifies its rows.
 * A column value, not a guess at the context shape: the watchdog's `context`
 * is its own business and may change without this line caring.
 */
export const FLEET_HEALTH_SOURCE = 'fleet-health'

/** The inbox, narrowed to the watchdog’s items. */
export const FLEET_HEALTH_HREF = `/inbox?source=${FLEET_HEALTH_SOURCE}`

/**
 * Where the watchdog's pass lives: `public.heartbeats`, source `watchdog` (its
 * fleet_components slug), read through `v_heartbeat_latest`. The watchdog
 * writes one on every pass, whether or not it found anything, and this is the
 * reason this module can tell "the fleet is fine" from "nothing has told us
 * anything for six hours".
 *
 * Findings alone cannot carry that. A watchdog that only files on trouble goes
 * silent exactly when it dies, and silence already means health here -- so a
 * dead watchdog would render `Fleet · running` forever, which is the failure
 * this whole line was built to stop, reintroduced one level up.
 *
 * It is a heartbeat rather than an always-open `approvals` row because a row
 * that never closes is a permanent item in an inbox whose whole premise is that
 * a row is something a person deals with. Until 24 Sep 2026 it was a
 * `watchdog.pass` event; the backfill moved those into heartbeats.
 */
export const WATCHDOG_HEARTBEAT_SOURCE = 'watchdog'

/**
 * How old the last pass may be before the line stops claiming to know.
 *
 * The watchdog's timer is every 10 minutes, so three missed passes is a real
 * outage rather than one slow run or a reboot. Reading freshness off a
 * timestamp the fleet wrote is the same move the Directory already makes with
 * `generated_at`: a page that has stopped being republished has to look
 * different from a fleet that has stopped changing.
 */
export const WATCHDOG_STALE_MS = 35 * 60 * 1000

/** What the read hands the mapper. Rows only -- no judgement is made there. */
export interface FleetHealth {
  /** Open watchdog findings, oldest first. Capped; `openCount` is not. */
  open: ApprovalRow[]
  /** How many are open in total, which is what the sentence counts. */
  openCount: number
  /**
   * When the watchdog last completed a pass, or null if it never has.
   *
   * Null is day one: before the watchdog first runs, the table looks exactly
   * like a healthy fleet, and asserting "running" into that is the lie this
   * line exists to stop.
   *
   * This replaces asking whether the watchdog had ever *filed* something, which
   * was the best available signal before the watchdog existed to ask. It was
   * also a long blind spot: a fleet that had been healthy since the day it was
   * built had no findings, so the line stayed blank until the first thing ever
   * went wrong. A pass proves the watchdog ran; a finding only proves it ran
   * and disliked what it saw.
   */
  lastPassAt: string | null
}

export interface FleetStatus {
  /** The whole line, as rendered: "Fleet · running". */
  line: string
  /** Clay when the fleet is not moving or we cannot tell; muted when it is. */
  tone: 'quiet' | 'fail'
  /** Where the line goes, or null when there is nothing to open. */
  href: string | null
}

/**
 * Long enough for a watchdog sentence, short enough to stay one line beside the
 * spend figures. The strip truncates again at its own width; this keeps the
 * "and 2 more" from being the part that gets cut.
 */
const LINE_MAX = 96

/**
 * @param read  the watchdog rows, or undefined while the read is in flight
 * @param failed  whether the read errored
 * @param now  the clock, injectable so staleness is testable
 */
export function toFleetStatus(
  read: FleetHealth | undefined,
  failed = false,
  now: number = Date.now(),
): FleetStatus | null {
  // A failed query is not a healthy fleet. Rendering "running" because we could
  // not read the table is the same lie as a directory that has stopped being
  // republished looking like a fleet that has stopped changing.
  if (failed) return { line: 'Fleet · status unknown — the inbox could not be read', tone: 'fail', href: null }

  // In flight. The strip's other figures render as "—" until they arrive; this
  // says nothing until it has something, rather than guessing for a second.
  if (!read) return null

  // Day one, suppressed: the watchdog has never completed a pass, so nothing
  // here is information, and "running" would be an assertion nothing supports.
  // Checked before the findings, because without a pass there is nothing to
  // believe about the rows either.
  if (!read.lastPassAt) return null

  // Findings first among the things we do know: an open finding is a fact the
  // watchdog asserted, and it stays the headline even if the watchdog has since
  // gone quiet. "Dispatch is stuck" is more use than "I am not sure", and the
  // two are not in conflict -- the finding was true when it was written.
  const oldest = read.open[0]
  if (!oldest) {
    // No findings. Now the question is whether silence means anything, and that
    // depends entirely on whether the watchdog is still reporting.
    const age = now - Date.parse(read.lastPassAt)
    if (!Number.isFinite(age) || age > WATCHDOG_STALE_MS) {
      return {
        line: 'Fleet · status unknown — the watchdog has stopped reporting',
        tone: 'fail',
        href: null,
      }
    }
    // Healthy is a clause, not an absence: a line that vanishes when things are
    // fine is indistinguishable from a line that has broken.
    return { line: 'Fleet · running', tone: 'quiet', href: null }
  }

  // The watchdog already wrote a sentence a person can act on. Paraphrasing it
  // into a category ("dispatch stalled") throws away the part that saves the
  // digging. Markers come out as text, as everywhere else agent prose is shown.
  const said = short(stripMarkers(oldest.summary ?? oldest.title), LINE_MAX)
  // A count of the rest, as a fact inside the sentence. Not a badge: product
  // rule 1 names the badge count specifically.
  const rest = Math.max(read.openCount - 1, 0)
  return {
    line: `Fleet · ${said}${rest > 0 ? ` and ${rest} more` : ''}`,
    tone: 'fail',
    href: FLEET_HEALTH_HREF,
  }
}
